import { SupplierProductImportBatchResult, suppliersAPI } from '../services/api';

export const SUPPLIER_PRODUCT_IMPORT_MAX_ROWS = 200;
export const SUPPLIER_PRODUCT_IMPORT_TARGET_BYTES = 64 * 1024;

export interface SupplierProductImportCheckpoint {
  importId: string;
  batches: any[][];
  nextBatchIndex: number;
  processedCount: number;
  importedCount: number;
  skippedCount: number;
  skipped: Array<{ rowNumber: number; reason: string }>;
}

export interface SupplierProductImportProgress {
  processedCount: number;
  totalCount: number;
  importedCount: number;
  skippedCount: number;
  batchIndex: number;
  batchCount: number;
}

type SendSupplierProductBatch = (
  supplierId: number,
  products: any[],
  importBatch: { importId: string; batchIndex: number },
) => Promise<SupplierProductImportBatchResult>;

const createImportId = () => {
  const cryptoApi = typeof globalThis !== 'undefined' ? globalThis.crypto : undefined;
  if (cryptoApi && typeof cryptoApi.randomUUID === 'function') {
    return cryptoApi.randomUUID();
  }

  const bytes = new Uint8Array(16);
  if (cryptoApi) {
    cryptoApi.getRandomValues(bytes);
  } else {
    for (let index = 0; index < bytes.length; index++) {
      bytes[index] = Math.floor(Math.random() * 256);
    }
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

const serializedBatchSize = (products: any[]) => new Blob([JSON.stringify({
  products,
  importId: '00000000-0000-4000-8000-000000000000',
  batchIndex: 999999,
})]).size;

export const buildSupplierProductImportBatches = (products: any[]) => {
  const batches: any[][] = [];
  let currentBatch: any[] = [];

  for (const product of products) {
    const candidate = [...currentBatch, product];
    const exceedsRowLimit = candidate.length > SUPPLIER_PRODUCT_IMPORT_MAX_ROWS;
    const exceedsByteTarget = serializedBatchSize(candidate) > SUPPLIER_PRODUCT_IMPORT_TARGET_BYTES;

    if (currentBatch.length > 0 && (exceedsRowLimit || exceedsByteTarget)) {
      batches.push(currentBatch);
      currentBatch = [product];
    } else {
      currentBatch = candidate;
    }
  }

  if (currentBatch.length > 0) {
    batches.push(currentBatch);
  }

  return batches;
};

export const createSupplierProductImportCheckpoint = (
  products: any[],
  importId = createImportId(),
): SupplierProductImportCheckpoint => ({
  importId,
  batches: buildSupplierProductImportBatches(products),
  nextBatchIndex: 0,
  processedCount: 0,
  importedCount: 0,
  skippedCount: 0,
  skipped: [],
});

export class SupplierProductImportError extends Error {
  constructor(
    message: string,
    public readonly checkpoint: SupplierProductImportCheckpoint,
    public readonly cause?: unknown,
  ) {
    super(message);
    this.name = 'SupplierProductImportError';
  }
}

export const runSupplierProductImport = async (
  supplierId: number,
  initialCheckpoint: SupplierProductImportCheckpoint,
  onProgress?: (progress: SupplierProductImportProgress, checkpoint: SupplierProductImportCheckpoint) => void,
  sendBatch: SendSupplierProductBatch = suppliersAPI.bulkImportProducts,
) => {
  let checkpoint = initialCheckpoint;
  const totalCount = checkpoint.batches.reduce((total, batch) => total + batch.length, 0);

  for (let batchIndex = checkpoint.nextBatchIndex; batchIndex < checkpoint.batches.length; batchIndex++) {
    const batch = checkpoint.batches[batchIndex];
    let result: SupplierProductImportBatchResult;

    try {
      result = await sendBatch(supplierId, batch, { importId: checkpoint.importId, batchIndex });
    } catch (cause: any) {
      const detail = cause?.response?.data?.message || cause?.message || 'The product import request failed.';
      throw new SupplierProductImportError(
        `Imported ${checkpoint.processedCount} of ${totalCount} products. ${detail} Retry to continue from this batch.`,
        checkpoint,
        cause,
      );
    }

    checkpoint = {
      ...checkpoint,
      nextBatchIndex: batchIndex + 1,
      processedCount: checkpoint.processedCount + batch.length,
      importedCount: checkpoint.importedCount + Number(result.importedCount || 0),
      skippedCount: checkpoint.skippedCount + Number(result.skippedCount || 0),
      skipped: [...checkpoint.skipped, ...(result.skipped || [])],
    };

    onProgress?.({
      processedCount: checkpoint.processedCount,
      totalCount,
      importedCount: checkpoint.importedCount,
      skippedCount: checkpoint.skippedCount,
      batchIndex: batchIndex + 1,
      batchCount: checkpoint.batches.length,
    }, checkpoint);
  }

  return checkpoint;
};
